import { useEffect, useMemo, useRef } from "react"
import { toast } from "react-toastify"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import {
  useLazyGetEnterpriseAlertsQuery,
  useLazyGetEnterpriseAlertStatusesWidgetQuery,
  useLazyGetEnterpriseAlertTypesWidgetQuery,
} from "store/api/enterpriseApi"
import { UITypes } from "types"

import { convertEnterpriseAlertsToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { ContentWrapper } from "components/common/ContentWrapper"
import { DateRangeTabsFilter } from "components/common/DateRangeTabsFilter"
import { getDateRangeParams } from "components/common/DateRangeTabsFilter/dateRangeFilterUtils"
import ExportButton from "components/common/ExportButton"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import TableWrapper from "components/table/TableWrapper"
import { AlertStatusesWidget } from "components/widgets/WidgetAlertStatuses"
import { MonitoringAlertsWidget } from "pages/contractor/ContractorDashboard/AlertsSection/WidgetMonitoringAlerts"

import { enterpriseAlertsTableHeaderCells } from "./enterpriseAlertsTableHeaderCells"

import styles from "./index.module.scss"

const TABLE_NAME = "contractor_alerts_list"
const DATE_PARAM_NAME = "date"

export const EnterpriseAlertsWidgets = () => {
  const [getTypes, types] = useLazyGetEnterpriseAlertTypesWidgetQuery()
  const [getStatuses, statuses] = useLazyGetEnterpriseAlertStatusesWidgetQuery()
  const { urlQueryParams } = useUrlQueryParams()

  useEffect(() => {
    const params = { date_start: urlQueryParams.date_start, date_end: urlQueryParams.date_end }
    getTypes(params)
    getStatuses(params)
  }, [getTypes, getStatuses, urlQueryParams.date_start, urlQueryParams.date_end])

  return (
    <div className={styles.widgetsWrapper}>
      <MonitoringAlertsWidget data={types.data?.data} isFetching={types.isFetching} />
      <AlertStatusesWidget data={statuses.data?.data} isFetching={statuses.isFetching} />
    </div>
  )
}

export const EnterpriseAlertsPage = () => {
  const [triggerGetAlerts, { isFetching, data }] = useLazyGetEnterpriseAlertsQuery()
  const [triggerGetTableColumns, { data: columnsData, isFetching: isColumnsFetching }] = useLazyGetTableColumnsQuery()
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const isFirstRunRef = useRef(true)

  const alertsList = data?.data ?? []
  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(enterpriseAlertsTableHeaderCells, columnsData.data.config)
        : enterpriseAlertsTableHeaderCells,
    [columnsData],
  )
  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: tableHeaderCells,
    meta: data?.meta,
    fileName: "enterprise-alerts",
    convertData: convertEnterpriseAlertsToTableRows,
    useLazyQuery: useLazyGetEnterpriseAlertsQuery,
  })

  useEffect(() => {
    const startKey = `${DATE_PARAM_NAME}_start`
    const endKey = `${DATE_PARAM_NAME}_end`
    if (!urlQueryParams[startKey] && !urlQueryParams[endKey]) {
      setUrlQueryParams(getDateRangeParams("last-7-days", DATE_PARAM_NAME))
    }
  }, [])

  useEffect(() => {
    triggerGetTableColumns({ list_name: TABLE_NAME })
  }, [triggerGetTableColumns])

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetAlerts(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false
      const startKey = `${DATE_PARAM_NAME}_start`
      const endKey = `${DATE_PARAM_NAME}_end`
      if (!urlQueryParams[startKey] && !urlQueryParams[endKey]) {
        return
      }
    }
    sendRequest(urlQueryParams)
  }, [urlQueryParams])

  return (
    <main className={styles.page}>
      <EnterpriseAlertsWidgets />
      <ContentWrapper
        title="Alerts History"
        renderHeaderAccessoryView={() => (
          <div className={styles.buttonsContainer}>
            <EditColumnsButton tableName={TABLE_NAME} tableHeaderCells={tableHeaderCells} />
            <ExportButton onClick={handleExport} disabled={data?.meta?.total === 0} />
          </div>
        )}
      >
        <div className={styles.dateFilterContainer}>
          <DateRangeTabsFilter
            paramName={DATE_PARAM_NAME}
            urlQueryParams={urlQueryParams}
            onParamsChange={setUrlQueryParams}
          />
        </div>
        <TableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={!isColumnsFetching}
          data={convertEnterpriseAlertsToTableRows(alertsList)}
          isLoading={isFetching}
          skippedTags={[DATE_PARAM_NAME]}
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
    </main>
  )
}

export default EnterpriseAlertsPage
